import React, { useState, useEffect } from 'react';
import {
  subscribeToPushNotifications,
  unsubscribeFromPushNotifications,
  isSubscribedToPush,
  sendTestNotification,
  areNotificationsSupported,
  getNotificationPermission
} from '../helpers/notifications';

function NotificationSettings() {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'success' or 'error'
  const [permission, setPermission] = useState('default');

  useEffect(() => {
    checkSubscriptionStatus();
    setPermission(getNotificationPermission());
  }, []);

  const checkSubscriptionStatus = async () => {
    setLoading(true);
    const subscribed = await isSubscribedToPush();
    setIsSubscribed(subscribed);
    setLoading(false);
  };

  const handleSubscribe = async () => {
    setLoading(true);
    setMessage('');
    
    const result = await subscribeToPushNotifications();
    
    if (result.success) {
      setIsSubscribed(true);
      setMessage('✓ Benachrichtigungen wurden aktiviert');
      setMessageType('success');
      setPermission(getNotificationPermission());
    } else {
      setMessage(`❌ Fehler: ${result.error}`);
      setMessageType('error');
    }
    
    setLoading(false);
  };

  const handleUnsubscribe = async () => {
    if (!window.confirm('Möchten Sie Benachrichtigungen wirklich deaktivieren?')) {
      return;
    }

    setLoading(true);
    setMessage('');
    
    const result = await unsubscribeFromPushNotifications();
    
    if (result.success) {
      setIsSubscribed(false);
      setMessage('✓ Benachrichtigungen wurden deaktiviert');
      setMessageType('success');
    } else {
      setMessage(`❌ Fehler: ${result.error}`);
      setMessageType('error');
    }
    
    setLoading(false);
  };

  const handleTestNotification = async () => {
    setLoading(true);
    setMessage('');
    
    const result = await sendTestNotification();
    
    if (result.success) {
      setMessage('✓ Test-Benachrichtigung wurde gesendet');
      setMessageType('success');
    } else {
      setMessage(`❌ Fehler: ${result.error}`);
      setMessageType('error');
    }
    
    setLoading(false);
  };

  if (!areNotificationsSupported()) {
    return (
      <div className="admin-section">
        <div className="section-header">
          <h2>🔔 Push-Benachrichtigungen</h2>
        </div>
        <div className="form-card">
          <div className="alert" style={{ background: '#fff3cd', borderLeft: '4px solid #ffc107', padding: '15px' }}>
            <strong>⚠️ Nicht unterstützt</strong>
            <p style={{ margin: '10px 0 0 0' }}>
              Ihr Browser unterstützt keine Push-Benachrichtigungen. 
              Bitte verwenden Sie Chrome, Firefox oder Edge auf Android.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2>🔔 Push-Benachrichtigungen</h2>
        <p className="section-description">
          Erhalten Sie Benachrichtigungen über wichtige Ereignisse
        </p>
      </div>

      {message && (
        <div 
          className={`alert ${messageType === 'success' ? 'alert-success' : 'alert-error'}`}
          style={{ margin: '20px 30px 0' }}
        >
          {message}
        </div>
      )}

      <div className="form-card">
        {permission === 'denied' ? (
          <div className="alert" style={{ background: '#f8d7da', borderLeft: '4px solid #dc3545', padding: '15px', marginBottom: '20px' }}>
            <strong>🚫 Benachrichtigungen blockiert</strong>
            <p style={{ margin: '10px 0 0 0' }}>
              Sie haben Benachrichtigungen für diese Website blockiert. 
              Bitte ändern Sie die Einstellung in Ihrem Browser, um Benachrichtigungen zu aktivieren.
            </p>
          </div>
        ) : (
          <>
            <div className="alert" style={{ background: '#e7f3ff', borderLeft: '4px solid #2196F3', padding: '15px', marginBottom: '20px' }}>
              <strong>📱 Benachrichtigungen erhalten für:</strong>
              <ul style={{ margin: '10px 0 0 20px', lineHeight: '1.8' }}>
                <li>⚠️ Gerät wurde als beschädigt markiert</li>
                <li>📅 Prüfung ist in 1 Woche fällig</li>
                <li>🚨 Gerät ist seit mehr als 2 Wochen überfällig</li>
              </ul>
            </div>

            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
              {isSubscribed ? (
                <>
                  <div style={{ 
                    padding: '10px 15px', 
                    background: '#d4edda', 
                    border: '1px solid #c3e6cb', 
                    borderRadius: '6px',
                    color: '#155724',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span style={{ fontSize: '20px' }}>✓</span>
                    <strong>Benachrichtigungen aktiviert</strong>
                  </div>
                  <button 
                    onClick={handleUnsubscribe} 
                    className="btn btn-secondary"
                    disabled={loading}
                  >
                    {loading ? '⏳ Wird deaktiviert...' : '🔕 Deaktivieren'}
                  </button>
                  <button 
                    onClick={handleTestNotification} 
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? '⏳ Wird gesendet...' : '🧪 Test-Benachrichtigung'}
                  </button>
                </>
              ) : (
                <>
                  <button 
                    onClick={handleSubscribe} 
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? '⏳ Wird aktiviert...' : '🔔 Benachrichtigungen aktivieren'}
                  </button>
                  {permission === 'default' && (
                    <div style={{ 
                      padding: '10px 15px', 
                      background: '#fff3cd', 
                      border: '1px solid #ffc107', 
                      borderRadius: '6px',
                      color: '#856404',
                      fontSize: '0.9em'
                    }}>
                      ℹ️ Sie werden nach der Berechtigung gefragt
                    </div>
                  )}
                </>
              )}
            </div>

            <div style={{ 
              marginTop: '20px', 
              padding: '15px', 
              background: '#f8f9fa', 
              borderRadius: '6px',
              fontSize: '0.9em',
              color: '#6c757d'
            }}>
              <strong>💡 Hinweis:</strong>
              <ul style={{ margin: '10px 0 0 20px', lineHeight: '1.6' }}>
                <li>Benachrichtigungen werden auch gesendet, wenn die App geschlossen ist</li>
                <li>Die App muss als PWA installiert sein für beste Ergebnisse</li>
                <li>Android Chrome wird empfohlen</li>
              </ul>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default NotificationSettings;
